import { Trash2 } from "lucide-react";
import { Button } from "../../../../components/ui/common/button";
import { Input } from "../../../../components/ui/common/input";
import { Label } from "../../../../components/ui/common/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../../components/ui/common/select";
import { ProductRegisterViewModel } from "../types/productRegisterView.types";

interface ProductUnitCardProps {
  vm: ProductRegisterViewModel;
  index: number;
}

/**
 * One sales-unit draft card — matches the Lovable prototype's "Sales unit N"
 * card exactly: eyebrow + delete icon (disabled when it's the last card),
 * a 4-field grid whose inputs write straight into `vm.salesUnits[index]`
 * (no separate mini-form/submit step). No per-unit image control here —
 * unlike the edit flow, a unit here has no real id until the whole product
 * is saved, so there's nothing yet to key a persisted image by.
 */
const ProductUnitCard = ({ vm, index }: ProductUnitCardProps) => {
  const unit = vm.salesUnits[index];
  const nameHint = vm.unitNameHint(index);

  return (
    <div className="tw-rounded-lg tw-border tw-border-border tw-bg-white tw-p-3">
      <div className="tw-flex tw-items-center tw-justify-between">
        <p className="tw-font-mono tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-muted-foreground">
          Sales unit {index + 1}
        </p>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="tw-h-7 tw-w-7 tw-text-destructive hover:tw-text-destructive"
          disabled={vm.salesUnits.length === 1}
          onClick={() => vm.onRemoveUnit(index)}
        >
          <Trash2 className="tw-h-3.5 tw-w-3.5" />
        </Button>
      </div>

      <div className="tw-mt-3 tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-4">
        <div className="tw-flex tw-flex-col tw-gap-1.5">
          <Label className="tw-text-xs">Unit Name *</Label>
          <Input
            className="tw-h-9 tw-text-sm"
            value={unit.sales_unit_name}
            onChange={e => vm.onUnitFieldChange(index, { sales_unit_name: e.target.value })}
            placeholder="Unit Name"
          />
          {nameHint ? <p className="tw-text-xs tw-text-muted-foreground">{nameHint}</p> : null}
        </div>
        <div className="tw-flex tw-flex-col tw-gap-1.5">
          <Label className="tw-text-xs">Unit Size</Label>
          <Input
            className="tw-h-9 tw-font-mono tw-text-sm"
            value={unit.sales_unit_size ?? ""}
            onChange={e => vm.onUnitFieldChange(index, { sales_unit_size: e.target.value })}
            placeholder="1"
          />
        </div>
        <div className="tw-flex tw-flex-col tw-gap-1.5">
          <Label className="tw-text-xs">Unit Type</Label>
          <Select
            value={unit.sales_unit_type}
            onValueChange={value => vm.onUnitFieldChange(index, { sales_unit_type: value })}
          >
            <SelectTrigger className="!tw-h-9 tw-w-full tw-font-mono tw-text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {vm.unitTypeOptions.map(option => (
                <SelectItem key={option.value} value={option.value} className="tw-font-mono">
                  {option.text}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="tw-flex tw-flex-col tw-gap-1.5">
          <Label className="tw-text-xs">EAN Barcode</Label>
          <Input
            className="tw-h-9 tw-font-mono tw-text-sm"
            value={unit.ean_barcode ?? ""}
            onChange={e => vm.onUnitFieldChange(index, { ean_barcode: e.target.value })}
            placeholder="optional"
          />
        </div>
      </div>
    </div>
  );
};

export default ProductUnitCard;
